import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Card, Form } from "react-bootstrap";
import FormikSelectField from "../../../../shared/components/FormikSelectField";



const TimeTriggerConfig = forwardRef(({ allowEnable = false, isEdit, initialData }, ref) => {
  // Initially disabled + unchecked
  const [enabled, setEnabled] = useState(false);
  const [triggerType, setTriggerType] = useState("after");
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(30);
  const [referenceTime, setReferenceTime] = useState("last_modified");
  const [businessHoursOnly, setBusinessHoursOnly] = useState(false);
  const [errors, setErrors] = useState({});

     // Auto-sync checkbox with executionMode
    useEffect(() => {
      if (allowEnable) {
        setEnabled(true);   // auto-check
      } else {
        setEnabled(false);  // auto-uncheck
      }
    }, [allowEnable]);

// Prefill when editing
useEffect(() => {
  if (isEdit && initialData) {
    const data = initialData;

    setEnabled(Boolean(data.time_trigger_enabled));
    setTriggerType(data.time_trigger_reference === "due_time" ? "before" : "after");
    setHours(Number(data.time_trigger_hours) || 0);
    setMinutes(Number(data.time_trigger_minutes) || 0);
    setReferenceTime(data.time_trigger_reference || "last_modified");
    setBusinessHoursOnly(Boolean(data.time_trigger_business_hours_only));
  }
}, [isEdit, initialData]);

  // Styled checkbox component
const StyledCheckbox = ({ label, checked, onChange, disabled }) => (
  <div className="col-md-6 my-auto">
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "1rem",
      }}
    >
      <input
        type="checkbox"
        className="custom-checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={!allowEnable}
        style={{
          width: "20px",
          height: "20px",
        }}
      />
      {label}
    </label>
  </div>
);

// Styled radio button component
const StyledRadio = ({ name, label, checked, onChange }) => (
  <div style={{ float: "left", marginRight: "15px" }}>
    <div style={{ fontSize: "1rem" }}>
      <label>
        <input
          type="radio"
          className="form-check-input me-2"
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={!enabled}
        />
        {label}
      </label>
    </div>
  </div>
);

  // Simple validation
  const validate = () => {
    const newErrors = {};
    if (enabled) {
      if (hours < 0 || minutes < 0) {
        newErrors.time = "Hours and minutes must be non-negative.";
      }
      if (!referenceTime) {
        newErrors.referenceTime = "Please select a reference time.";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Expose function for parent validation
  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      const isValid = validate();
      const data = {
        enabled,
        triggerType,
        hours: Number(hours),
        minutes: Number(minutes),
        referenceTime,
        businessHoursOnly,
      };
      return { isValid, data };
    },
    // future use: allow parent to enable trigger programmatically
    enableTrigger: () => setEnabled(true),
    disableTrigger: () => setEnabled(false),
  }));

  return (
    <Card
      className="border-0 shadow-sm rounded-3 p-4"
      style={{ background: "#fff" }}
    >
      <Card.Header>Time Trigger Configuration</Card.Header>
      <Card.Body>
        {/* Enable Trigger Checkbox */}
        <div
          className="d-flex align-items-center mb-3 p-2 rounded"
          style={{ background: "#fff" }}
        >
          <StyledCheckbox
            label="Enable Time Trigger"
            checked={enabled}
            onChange={setEnabled}
            disabled={!allowEnable} // <-- locked; user cannot toggle manually
          />
        </div>

        {/* <h5 className="fw-semibold mb-2">Time Trigger Configuration</h5> */}
        {/* <hr className="mt-0 mb-4" /> */}

        {/* AFTER Trigger */}
        <div className="d-flex align-items-center mb-4">
          <StyledRadio
            name="triggerType"
            label=""
            checked={triggerType === "after"}
            onChange={() => setTriggerType("after")}
          />

          <div className="d-flex align-items-center flex-wrap gap-2">
            <span>Escalate when more than</span>
            <Form.Control
              type="number"
              value={triggerType === "after" ? hours : 0}
              min="0"
              max="23"
              onChange={(e) => setHours(e.target.value)}
              style={{ width: "70px" }}
              disabled={!enabled || triggerType !== "after"}
            />
            <span>hours and</span>
            <Form.Control
              type="number"
              value={triggerType === "after" ? minutes : 0}
              min="0"
              max="59"
              onChange={(e) => setMinutes(e.target.value)}
              style={{ width: "70px" }}
              disabled={!enabled || triggerType !== "after"}
            />
            <span>minutes have passed since</span>

            <div style={{ width: "200px" }}>
              <FormikSelectField
                name="referenceTimeAfter"
                value={referenceTime}
                onChange={(name, value) => setReferenceTime(value)}
                disabled={!enabled || triggerType !== "after"}
              >
                <option value="last_modified">Last modified time</option>
                <option value="created">Created time</option>
                <option value="status_changed">Status changed time</option>
              </FormikSelectField>
            </div>
          </div>
        </div>

        {/* BEFORE Trigger */}
        <div className="d-flex align-items-center mb-4">
          <StyledRadio
            name="triggerType"
            label=""
            checked={triggerType === "before"}
            onChange={() => setTriggerType("before")}
          />

          <div className="d-flex align-items-center flex-wrap gap-2">
            <Form.Control
              type="number"
              value={triggerType === "before" ? hours : 0}
              min="0"
              max="23"
              onChange={(e) => setHours(e.target.value)}
              style={{ width: "70px" }}
              disabled={!enabled || triggerType !== "before"}
            />
            <span>hours and</span>
            <Form.Control
              type="number"
              value={triggerType === "before" ? minutes : 0}
              min="0"
              max="59"
              onChange={(e) => setMinutes(e.target.value)}
              style={{ width: "70px" }}
              disabled={!enabled || triggerType !== "before"}
            />
            <span>minutes before</span>

            <div style={{ width: "200px" }}>
              <FormikSelectField
                name="referenceTimeBefore"
                value={triggerType === "before" ? referenceTime : "due_time"}
                onChange={(name, value) => setReferenceTime(value)}
                disabled={!enabled || triggerType !== "before"}
              >
                <option value="due_time">Due time</option>
              </FormikSelectField>
            </div>
          </div>
        </div>

        {/* Business Hours Checkbox */}
        <StyledCheckbox
          label="Calculate based on business hours only"
          checked={businessHoursOnly}
          onChange={setBusinessHoursOnly}
          disabled={!enabled}
        />

        {/* Validation messages */}
        {Object.keys(errors).length > 0 && (
          <div className="text-danger mt-2" style={{ fontSize: "0.9rem" }}>
            {Object.values(errors).map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
});

export default TimeTriggerConfig;
